import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Loader2, Globe, ArrowLeft, Eye, EyeOff } from 'lucide-react';

export default function ResetPassword() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isPasswordFocused, setIsPasswordFocused] = useState(false);
    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const [error, setError] = useState('');

    useEffect(() => {
        const lang = searchParams.get('lang');
        if (lang && (lang === 'tr' || lang === 'en' || lang === 'de')) {
            i18n.changeLanguage(lang);
        }
    }, []); // Run only once on mount

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setError('');
    };

    const onSubmit = async (e) => {
        e.preventDefault();

        if (!token) {
            setError('auth.invalidToken');
            return;
        }

        const password = formData.newPassword;
        const requirementsMet =
            /[A-Z]/.test(password) &&
            /[a-z]/.test(password) &&
            /[!@#$%^&*(),.?":{}|<>]/.test(password) &&
            password.length >= 8;

        if (!requirementsMet) {
            setError('auth.passwordRequirements');
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            setError('auth.passwordsDoNotMatch');
            return;
        }

        setLoading(true);
        try {
            await authAPI.resetPassword({
                token,
                new_password: formData.newPassword
            });
            toast.success(t('auth.passwordResetSuccess'));
            setTimeout(() => navigate('/login'), 2000);
        } catch (err) {
            setError(err.response?.data?.detail || 'common.operationFailed');
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-[#f0f2f5] p-4">
                <Card className="w-full max-w-[400px] bg-white border-0 shadow-sm text-center p-8">
                    <CardTitle className="text-xl font-bold text-red-500 mb-4">
                        {t('auth.invalidLink', { defaultValue: 'Geçersiz Bağlantı' })}
                    </CardTitle>
                    <p className="text-gray-500 mb-6 font-medium">
                        {t('auth.invalidLinkSubtitle', { defaultValue: 'Şifre sıfırlama bağlantısı geçersiz veya süresi dolmuş.' })}
                    </p>
                    <Link to="/forgot-password">
                        <Button className="w-full bg-[#1a1a1a] hover:bg-black text-white">
                            {t('auth.getNewLink', { defaultValue: 'Yeni Bağlantı İste' })}
                        </Button>
                    </Link>
                </Card>
            </div>
        );
    }

    const password = formData.newPassword;
    const requirementsMet =
        /[A-Z]/.test(password) &&
        /[a-z]/.test(password) &&
        /[!@#$%^&*(),.?":{}|<>]/.test(password) &&
        password.length >= 8;

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-[#f0f2f5] p-4 relative">
            <div className="absolute top-4 right-4">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="rounded-lg">
                            <Globe className="w-5 h-5 text-gray-600" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-white">
                        <DropdownMenuItem onClick={() => i18n.changeLanguage('tr')}>Türkçe</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => i18n.changeLanguage('en')}>English</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => i18n.changeLanguage('de')}>Deutsch</DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            <div className="flex flex-col items-center mb-4 text-center">
                <div className="w-48 h-48 flex items-center justify-center -mb-6">
                    <img src="/logo.png" alt="Connected Mobile" className="w-full h-full object-contain" />
                </div>
                <h1 className="text-lg font-bold text-gray-900">{t('auth.brandName', { defaultValue: 'Contract Manager' })}</h1>
                <p className="text-gray-500 text-sm">www.connectedmobile.de</p>
            </div>

            <Card className="w-full max-w-[400px] bg-white border-0 shadow-sm">
                <CardHeader className="space-y-1 pb-4">
                    <CardTitle className="text-2xl font-bold text-left">
                        {t('auth.resetPassword', { defaultValue: 'Yeni Şifre Belirle' })}
                    </CardTitle>
                    <CardDescription className="text-left text-gray-500 font-medium">
                        {t('auth.resetPasswordSubtitle', { defaultValue: 'Lütfen yeni şifrenizi giriniz.' })}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={onSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="newPassword">{t('auth.newPassword', { defaultValue: 'Yeni Şifre' })}</Label>
                            <div className="relative">
                                <Input
                                    id="newPassword"
                                    name="newPassword"
                                    type={showPassword ? "text" : "password"}
                                    value={formData.newPassword}
                                    onChange={handleInputChange}
                                    onFocus={() => setIsPasswordFocused(true)}
                                    onBlur={() => setIsPasswordFocused(false)}
                                    required
                                    className="h-11 border-gray-200 focus:border-black focus:ring-black pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>

                        {/* Unified Requirement Message - Visible only on focus */}
                        {isPasswordFocused && (
                            <div className="py-1 px-1">
                                <p className={`text-[12px] font-medium leading-relaxed ${requirementsMet ? 'text-green-600' : 'text-red-500'}`}>
                                    {t('auth.passwordRequirements')}
                                </p>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">{t('auth.confirmPassword', { defaultValue: 'Şifre Tekrar' })}</Label>
                            <Input
                                id="confirmPassword"
                                name="confirmPassword"
                                type="password"
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                required
                                className="h-11 border-gray-200 focus:border-black focus:ring-black"
                            />
                        </div>

                        {error && (
                            <p className="text-xs text-red-500 font-medium mt-1">
                                {error.includes('.') ? t(error) : error}
                            </p>
                        )}

                        <Button
                            type="submit"
                            className="w-full bg-[#1a1a1a] hover:bg-black text-white font-bold h-11 mt-4"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    {t('common.loading')}
                                </>
                            ) : (
                                t('common.save', { defaultValue: 'Şifreyi Güncelle' })
                            )}
                        </Button>

                        <div className="text-center pt-2">
                            <Link
                                to="/login"
                                className="text-sm font-medium text-gray-600 hover:text-black inline-flex items-center gap-2"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                {t('auth.backToLogin')}
                            </Link>
                        </div>
                    </form>
                </CardContent>
            </Card>

            <div className="mt-8 text-center text-xs text-gray-300 font-medium">
                {t('auth.brandFooter', { defaultValue: 'Hand crafted & made by ZeuZ-IT' })}
            </div>
        </div>
    );
}
